import { useCallback, useRef, useState, type ReactNode } from 'react'
import { Botao, Campo, Modal } from './ui'

/**
 * Substitui `confirm()` e `prompt()` do navegador.
 *
 * Não é questão de estilo: os diálogos nativos **não aparecem** em parte dos
 * navegadores e contextos, e quando não aparecem a chamada devolve `null` ou
 * `false` em silêncio. Foi o que fez metade dos botões de renomear e excluir
 * do sistema parecerem quebrados — a requisição estava certa e nunca chegava
 * a ser disparada.
 *
 * A API é em promessa para a troca ser direta no código que já existia:
 * `if (confirm(x))` vira `if (await pedirConfirmacao(x))`.
 */

interface PedidoTexto {
  tipo: 'texto'
  titulo: string
  valor: string
  rotulo: string
  resolver: (valor: string | null) => void
}

interface PedidoConfirmacao {
  tipo: 'confirmacao'
  titulo: string
  mensagem: ReactNode
  rotuloConfirmar: string
  perigo: boolean
  resolver: (ok: boolean) => void
}

type Pedido = PedidoTexto | PedidoConfirmacao

export function usePerguntar() {
  const [pedido, setPedido] = useState<Pedido | null>(null)
  const [rascunho, setRascunho] = useState('')
  // Guarda o pedido em curso para o fechamento nunca deixar a promessa pendurada.
  const emCurso = useRef<Pedido | null>(null)

  const encerrar = useCallback((resposta: string | null | boolean) => {
    const atual = emCurso.current
    emCurso.current = null
    setPedido(null)
    if (!atual) return
    if (atual.tipo === 'texto') atual.resolver(typeof resposta === 'string' ? resposta : null)
    else atual.resolver(resposta === true)
  }, [])

  const pedirTexto = useCallback(
    (titulo: string, valorInicial = '', rotulo = 'Nome') =>
      new Promise<string | null>((resolver) => {
        const novo: PedidoTexto = { tipo: 'texto', titulo, valor: valorInicial, rotulo, resolver }
        emCurso.current = novo
        setRascunho(valorInicial)
        setPedido(novo)
      }),
    [],
  )

  const pedirConfirmacao = useCallback(
    (
      mensagem: ReactNode,
      opcoes?: { titulo?: string; rotuloConfirmar?: string; perigo?: boolean },
    ) =>
      new Promise<boolean>((resolver) => {
        const novo: PedidoConfirmacao = {
          tipo: 'confirmacao',
          titulo: opcoes?.titulo ?? 'Confirmar',
          mensagem,
          rotuloConfirmar: opcoes?.rotuloConfirmar ?? 'Confirmar',
          perigo: opcoes?.perigo ?? true,
          resolver,
        }
        emCurso.current = novo
        setPedido(novo)
      }),
    [],
  )

  const dialogo = pedido ? (
    <Modal
      aberto
      titulo={pedido.titulo}
      aoFechar={() => encerrar(pedido.tipo === 'texto' ? null : false)}
      // Formulário curto, mas ainda é digitação: clicar fora não descarta.
      fecharAoClicarFora={pedido.tipo !== 'texto'}
      rodape={
        <>
          <Botao onClick={() => encerrar(pedido.tipo === 'texto' ? null : false)}>Cancelar</Botao>
          <Botao
            variante={pedido.tipo === 'confirmacao' && pedido.perigo ? 'perigo' : 'primario'}
            onClick={() => encerrar(pedido.tipo === 'texto' ? rascunho.trim() : true)}
            disabled={pedido.tipo === 'texto' && rascunho.trim() === ''}
          >
            {pedido.tipo === 'texto' ? 'Salvar' : pedido.rotuloConfirmar}
          </Botao>
        </>
      }
    >
      {pedido.tipo === 'texto' ? (
        <Campo
          rotulo={pedido.rotulo}
          value={rascunho}
          autoFocus
          onChange={(e) => setRascunho(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && rascunho.trim() !== '') encerrar(rascunho.trim())
          }}
        />
      ) : (
        <p className="text-sm text-tinta-600">{pedido.mensagem}</p>
      )}
    </Modal>
  ) : null

  return { pedirTexto, pedirConfirmacao, dialogo }
}
